import {Box, CircularProgress, IconButton, InputAdornment, TextField, Typography} from '@mui/material'
import {AttachFile, Close, Send} from '@mui/icons-material'
import {AttachmentState, ATTACHMENT_ACCEPT} from './ReplyAttachmentUploader'

const MAX_MESSAGE_LENGTH = 1000

export function ReplyForm({ replyText, setReplyText, isSending, sendError, attachment, setAttachment, onSubmit }: {
  replyText: string;
  setReplyText: (text: string) => void;
  isSending: boolean;
  sendError: unknown;
  attachment: AttachmentState;
  setAttachment: (state: AttachmentState) => void;
  onSubmit: (e?: React.FormEvent) => void;
}) {
  function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setAttachment({status: 'uploading', file})
  }

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{
        position: 'fixed',
        bottom: { xs: '56px', sm: 0 },
        left: { xs: 0, sm: '270px' },
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.5,
        bgcolor: 'white',
        boxShadow: '0 -1px 4px rgba(0,0,0,0.1)',
        zIndex: 10,
      }}
    >
      {attachment.status !== 'idle' && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            maxWidth: '100%',
            alignSelf: 'flex-start',
            bgcolor: 'rgba(0,0,0,0.06)',
            borderRadius: '8px',
            px: 1.25,
            py: 0.5,
          }}
        >
          <AttachFile fontSize="small"/>
          <Typography variant="body2" sx={{wordBreak: 'break-word'}}>
            {attachment.file.name}
          </Typography>
          {attachment.status === 'uploading' && <CircularProgress size={16}/>}
          {attachment.status === 'error' && (
            <Typography variant="teeny" color="error">{attachment.message}</Typography>
          )}
          <IconButton
            aria-label="Remove attachment"
            size="small"
            onClick={() => setAttachment({status: 'idle'})}
          >
            <Close fontSize="small"/>
          </IconButton>
        </Box>
      )}

      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type your reply..."
          value={replyText}
          onChange={e => setReplyText(e.target.value)}
          disabled={isSending}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              onSubmit()
            }
          }}
          error={!!sendError}
          helperText={sendError ? 'Failed to send. Please try again.' : `${MAX_MESSAGE_LENGTH - replyText.length} characters`}
          inputProps={{maxLength: MAX_MESSAGE_LENGTH}}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Attach file"
                  component="label"
                  disabled={isSending || attachment.status === 'uploading'}
                  edge="start"
                >
                  <AttachFile/>
                  <input type="file" hidden accept={ATTACHMENT_ACCEPT} onChange={handleFileSelected}/>
                </IconButton>
                <IconButton
                  aria-label="Send reply"
                  type="submit"
                  color="primary"
                  disabled={!replyText.trim() || isSending || attachment.status === 'uploading'}
                  edge="end"
                >
                  {isSending ? <CircularProgress size={24} color="primary"/> : <Send/>}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{'& .MuiOutlinedInput-root': {borderRadius: '12px'}}}
        />
      </Box>
    </Box>
  )
}
